/* KCI Event Tagging › KCI Dashboard (KCI_API_DOC §7, mockup Dashboard).
   Landing page: KPI tiles, day-on-day trends (stacked by parent courier), reason /
   direction / scope breakdowns, top pincodes, per-courier impact, and the all-events
   list (search + per-row CSV exports). Date range drives every widget. */
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import {
  Box, Grid, Stack, Typography, Button, Chip, CircularProgress, Tooltip,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, Legend, LabelList,
} from 'recharts'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import PageTitle from '../../components/PageTitle'
import DateRangePicker from '../../components/DateRangePicker'
import TaggedOrdersModal from './TaggedOrdersModal'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import OrderRowActions from '../../common/OrderRowActions'
import { apiConstants, getDownload } from '../../common/constants'
import { FilterCard, FilterCardContent, FilterRow, FilterField, FieldLabel, FilterActions, ApplyButton, ResetButton } from '../../common/FilterBar'
import {
  useDashKpisQuery, useDashTrendsQuery, useDashReasonsQuery, useDashByCourierQuery,
  useDashByScopeQuery, useDashByDirectionQuery, useDashTopPincodesQuery, useSearchEventsQuery,
  useTaggedOrdersQuery,
} from '../../redux/kciApiSlice'
import { reasonLabel, reasonColor, directionShort, STATUS_COLOR } from './kciConstants'
import { useForm } from 'react-hook-form'
import { FormProvider, RHFSearchableSelectField } from '../../common/hook-form'
import {
  SectionCard, StatTile, gridSx, gridWrapSx, CellLine, CellStack, MUTED, NAVY, errMsg, dirTone, Pill, CoverageCell, toKey, parseKey,
} from './kciUi'

const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10)
const today = () => new Date().toISOString().slice(0, 10)

// Month/year picker helpers for the trend charts (drive their own from/to → API).
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const curMonthKey = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` }
// guard against an empty/invalid key — new Date(NaN).toISOString() throws and blanks the page
const monthRange = (key) => {
  const safe = /^\d{4}-\d{2}$/.test(key || '') ? key : curMonthKey()
  const [y, m] = safe.split('-').map(Number)
  return { from: `${safe}-01`, to: new Date(y, m, 0).toISOString().slice(0, 10) }
}
const buildMonthOptions = () => {
  const opts = []; const d = new Date()
  for (let i = 0; i < 12; i++) { const y = d.getFullYear(); const m = d.getMonth(); opts.push({ value: `${y}-${String(m + 1).padStart(2, '0')}`, label: `${MONTHS[m]} ${y}` }); d.setMonth(d.getMonth() - 1) }
  return opts
}
const prevMonthKey = (key) => {
  const safe = /^\d{4}-\d{2}$/.test(key || '') ? key : curMonthKey()
  const [y, m] = safe.split('-').map(Number)
  const d = new Date(y, m - 2, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
const pctDelta = (cur, prev) => (prev ? ((cur - prev) / prev) * 100 : null)

const ChartCard = ({ title, children }) => <SectionCard title={title}>{children}</SectionCard>
const EmptyChart = ({ label }) => (
  <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>{label}</Typography>
  </Box>
)

// Pivot the /dashboard/trends rollup rows → { data:[{ date:'MM-DD', [parent]:value }], parents }.
// parentCourier is bucketed to a canonical parent so colours/legend match the mockup.
const pivotTrends = (rows, metric) => {
  const byDate = {}; const parents = new Set()
  ;(rows || []).forEach((r) => {
    const day = String(r.date || '').slice(5, 10); if (!day) return
    const p = canonParent(r.parentCourier); parents.add(p)
    byDate[day] = byDate[day] || { date: day }
    byDate[day][p] = (byDate[day][p] || 0) + (r[metric] || 0)
  })
  return { data: Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)), parents: [...parents] }
}
const sumMetric = (rows, metric) => (rows || []).reduce((s, r) => s + (r[metric] || 0), 0)
// Fixed courier → colour map (mockup): BlueDart purple, Xpressbees orange,
// Delhivery teal, rest fall back to a stable palette; unknowns → grey "Others".
const COURIER_COLORS = { BlueDart: '#6D28D9', Xpressbees: '#EA580C', Delhivery: '#0D9488', DTDC: '#0EA5E9', Ekart: '#DB2777', Swift: '#B45309', ElasticRun: '#16A34A', Pikndel: '#7C3AED', Others: '#94A3B8', Other: '#94A3B8' }
const FALLBACK_COLORS = ['#0EA5E9', '#DB2777', '#B45309', '#16A34A', '#7C3AED']
const courierColor = (name, i = 0) => COURIER_COLORS[name] || FALLBACK_COLORS[i % FALLBACK_COLORS.length]
// Bucket a raw courier/account name (e.g. "XpressBees testing Ankit", "Delhivery_ENT_SURFACE")
// into its parent so the series + colours match the mockup; unknown → "Others".
const canonParent = (name) => {
  const s = (name || '').toLowerCase()
  if (s.includes('bluedart') || s.includes('blue dart')) return 'BlueDart'
  if (s.includes('xpressbees') || s.includes('xpress bees')) return 'Xpressbees'
  if (s.includes('delhivery')) return 'Delhivery'
  if (s.includes('dtdc')) return 'DTDC'
  if (s.includes('ekart')) return 'Ekart'
  if (s.includes('swift')) return 'Swift'
  if (s.includes('elasticrun') || s.includes('elastic run')) return 'ElasticRun'
  if (s.includes('pikndel')) return 'Pikndel'
  return 'Others'
}

const objToBars = (obj, labeler = (k) => k, colorer) =>
  Object.entries(obj || {}).map(([k, v]) => ({ name: labeler(k), value: v, fill: colorer ? colorer(k) : '#6D28D9' }))

// Mockup axis/legend styling: grey readings on both axes, day-of-month X ticks,
// bottom-left legend with small round dots + dark courier labels.
const AXIS_TICK = { fontSize: 11, fill: '#8A94A6' }
const CAT_TICK = { fontSize: 11, fill: '#637381' }
const dayTick = (v) => String(Number(String(v).slice(-2)))
const legendFmt = (v) => <span style={{ color: '#212B36', fontSize: 12, marginRight: 14 }}>{v}</span>
// value label drawn on top of each bar (mockup shows the number above every bar)
const barLabelStyle = { fontSize: 12, fontWeight: 700, fill: '#212B36' }
// short reason labels for the X axis (mockup: "Infra", "Religious/Festival", …)
const SHORT_REASON = { WEATHER: 'Weather', RELIGIOUS_FESTIVAL: 'Religious/Festival', POLITICAL_CIVIC: 'Political/Civic', INFRASTRUCTURE: 'Infra', COURIER_INTERNAL: 'Courier', ROUTE_LINEHAUL: 'Route', ADMINISTRATIVE: 'Admin', OTHER: 'Other' }
const PURPLE = '#6D28D9'
const PURPLE_LIGHT = '#A98AE6'

export default function KciDashboard() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  // draft = what the date fields show; range = applied window that drives the API (set on "Filter")
  const [from, setFrom] = useState(daysAgo(30))
  const [to, setTo] = useState(today())
  const [range, setRange] = useState({ from: daysAgo(30), to: today() })
  const [taggedFor, setTaggedFor] = useState(null)
  const applyFilters = () => setRange({ from, to })
  const resetFilters = () => { const d = daysAgo(30), t = today(); setFrom(d); setTo(t); setRange({ from: d, to: t }) }
  // per-trend-chart month + courier pickers (mockup: two dropdowns per chart) —
  // held in react-hook-form so they render via the shared RHFSearchableSelectField.
  const methods = useForm({ defaultValues: { eventsMonth: curMonthKey(), shipMonth: curMonthKey(), eventsCourier: '', shipCourier: '' } })
  const eventsMonth = methods.watch('eventsMonth')
  const shipMonth = methods.watch('shipMonth')
  const eventsCourier = methods.watch('eventsCourier')
  const shipCourier = methods.watch('shipCourier')
  const monthOptions = useMemo(buildMonthOptions, [])
  const eventsRange = useMemo(() => monthRange(eventsMonth), [eventsMonth])
  const shipRange = useMemo(() => monthRange(shipMonth), [shipMonth])
  const eventsPrevRange = useMemo(() => monthRange(prevMonthKey(eventsMonth)), [eventsMonth])
  const shipPrevRange = useMemo(() => monthRange(prevMonthKey(shipMonth)), [shipMonth])

  const { data: kpis, isLoading: kpisLoading, isError: kpisError, error: kpisErrObj } = useDashKpisQuery(range)
  const { data: trends, isLoading: trendsLoading } = useDashTrendsQuery(range)
  // Daily-trend charts source from the dedicated /dashboard/trends rollup (KCI_API_DOC §7),
  // per month, with the previous month fetched for the vs-prev-month trend chip.
  const { data: eventsTrends } = useDashTrendsQuery(eventsRange)
  const { data: shipTrends } = useDashTrendsQuery(shipRange)
  const { data: eventsPrevTrends } = useDashTrendsQuery(eventsPrevRange)
  const { data: shipPrevTrends } = useDashTrendsQuery(shipPrevRange)
  const { data: reasons } = useDashReasonsQuery(range)
  const { data: byCourier, isLoading: byCourierLoading } = useDashByCourierQuery({ ...range, parentRollup: true })
  const { data: byScope } = useDashByScopeQuery(range)
  const { data: byDirection } = useDashByDirectionQuery(range)
  const { data: topPincodes } = useDashTopPincodesQuery({ ...range, limit: 10 })
  const { data: eventsRes, isLoading: eventsLoading, isError: eventsError, error: eventsErrObj } = useSearchEventsQuery({ ...range, page: 1, size: 10 })
  const initialLoading = kpisLoading || trendsLoading || byCourierLoading || eventsLoading
  // "Orders tagged" KPI: the /kpis field (ordersTaggedThisMonth) is month-locked and
  // ignores from/to, so source the range-scoped count from tagged-orders' totalRecords.
  const { data: taggedInWindow } = useTaggedOrdersQuery({ ...range, page: 1, size: 1 })

  useEffect(() => {
    if (eventsError) enqueueSnackbar(errMsg(eventsErrObj, 'Could not load KCI events.'), { variant: 'error' })
  }, [eventsError, eventsErrObj, enqueueSnackbar])
  useEffect(() => {
    if (kpisError) enqueueSnackbar(errMsg(kpisErrObj, 'Could not load KPI cards (dashboard/kpis).'), { variant: 'error' })
  }, [kpisError, kpisErrObj, enqueueSnackbar])

  const events = eventsRes?.events || []
  const eventsTotal = eventsRes?.totalRecords ?? events.length
  const eventsRaised = useMemo(() => pivotTrends(eventsTrends, 'eventsRaisedCount'), [eventsTrends])
  const shipmentsTagged = useMemo(() => pivotTrends(shipTrends, 'shipmentsTaggedCount'), [shipTrends])
  // series shown = all (stacked) or a single selected courier
  // empty courier = all series (stacked); a picked courier filters to that one
  const eventsSeries = !eventsCourier ? eventsRaised.parents : eventsRaised.parents.filter((p) => p === eventsCourier)
  const shipSeries = !shipCourier ? shipmentsTagged.parents : shipmentsTagged.parents.filter((p) => p === shipCourier)
  const eventsDelta = pctDelta(sumMetric(eventsTrends, 'eventsRaisedCount'), sumMetric(eventsPrevTrends, 'eventsRaisedCount'))
  const shipDelta = pctDelta(sumMetric(shipTrends, 'shipmentsTaggedCount'), sumMetric(shipPrevTrends, 'shipmentsTaggedCount'))
  const reasonBars = useMemo(() => objToBars(reasons, (k) => SHORT_REASON[k] || reasonLabel(k), reasonColor), [reasons])
  // fixed categories so both bars always render (even when one side is 0), like the mockup
  const directionBars = useMemo(() => [
    { name: 'Forward', value: byDirection?.FORWARD || 0 },
    { name: 'Reverse', value: byDirection?.REVERSE || 0 },
  ], [byDirection])
  const scopeBars = useMemo(() => [
    { name: 'Account-specific', value: byScope?.SPECIFIC_ACCOUNT || 0 },
    { name: 'Parent-wide', value: byScope?.PARENT_WIDE || 0 },
  ], [byScope])

  // per-parent-courier impact table ("KCI impact history"): Events / Pincodes / Days
  // now come per courier from /by-courier ({ couriers: [...] }); Shipments are still
  // summed from the daily trend rows.
  const courierRows = useMemo(() => {
    const m = {}
    ;(byCourier?.couriers || []).forEach((c) => {
      m[c.courier] = { courier: c.courier, events: c.eventsRaised ?? 0, shipments: 0, panIndia: !!c.panIndia, pincodes: c.pincodesImpacted ?? 0, days: c.daysImpacted ?? 0 }
    })
    ;(trends || []).forEach((r) => {
      const k = r.parentCourier; if (!k) return
      if (!m[k]) m[k] = { courier: k, events: r.eventsRaisedCount || 0, shipments: 0, panIndia: false, pincodes: 0, days: 0 }
      m[k].shipments += r.shipmentsTaggedCount || 0
    })
    return Object.values(m)
  }, [byCourier, trends])
  const courierTotals = useMemo(() => courierRows.reduce((t, r) => ({
    events: t.events + r.events, shipments: t.shipments + r.shipments,
    pincodes: t.pincodes + (r.panIndia ? 0 : r.pincodes), days: t.days + r.days, anyPan: t.anyPan || r.panIndia,
  }), { events: 0, shipments: 0, pincodes: 0, days: 0, anyPan: false }), [courierRows])

  const exportUrl = (id, kind) => apiConstants.BASE_LOCAL + `kci/event/${id}/export/${kind}`
  const doExport = async (id, kind) => {
    try {
      const res = await getDownload(exportUrl(id, kind))
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url; a.download = `kci_${id}_${kind}.csv`; document.body.appendChild(a); a.click(); a.remove()
      window.URL.revokeObjectURL(url)
    } catch { /* surfaced via the empty download; backend errors show in the network tab */ }
  }

  const pincodesImpacted = kpis?.pincodesImpacted
  const pinDisplay = pincodesImpacted === 'PAN_INDIA' ? 'Pan-India' : (pincodesImpacted ?? '—')

  // title + "▲/▼ X% vs prev month" chip (green up / red down); hidden when no prior data
  const trendTitle = (label, delta) => (
    <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {label}
      {delta != null && (
        <Box component='span' sx={{ fontSize: 10.5, fontWeight: 700, px: 0.9, py: 0.2, borderRadius: '999px',
          color: delta >= 0 ? '#15803d' : '#b91c1c', bgcolor: delta >= 0 ? '#e9f6ee' : '#fdecec' }}>
          {delta >= 0 ? '▲' : '▼'} {delta >= 0 ? '+' : ''}{delta.toFixed(0)}% vs prev month
        </Box>
      )}
    </Box>
  )

  // per-chart controls (mockup): month/year picker + courier picker.
  const ctrlSx = { minWidth: 150, '& .MuiInputBase-input': { fontSize: 12, py: '7px' } }
  const trendControls = (monthName, courierName, parents) => (
    <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
      <RHFSearchableSelectField name={monthName} label='' sx={ctrlSx} placeholder='Select month' options={monthOptions} />
      <RHFSearchableSelectField name={courierName} label='' sx={ctrlSx} placeholder='All couriers (stacked)'
        options={parents.map((p) => ({ value: p, label: `${p} only` }))} />
    </Stack>
  )

  const eventColumns = [
    {
      field: 'title', headerName: 'Event', flex: 1.4, sortable: false,
      renderCell: (p) => (
        <CellStack>
          <CellLine bold>{p.row.title || '—'}</CellLine>
          <CellLine muted ellipsis>{p.row.id}</CellLine>
        </CellStack>
      ),
    },
    {
      field: 'courier', headerName: 'Courier', flex: 1, sortable: false,
      renderCell: (p) => <CellLine>{p.row.parentCourier || p.row.courierCode || '—'}</CellLine>,
    },
    {
      field: 'direction', headerName: 'Direction', flex: 0.8, sortable: false,
      renderCell: (p) => <Pill tone={dirTone(p.row.direction)} dot>{directionShort(p.row.direction)}</Pill>,
    },
    {
      field: 'coverage', headerName: 'Coverage / Pincodes', flex: 1.1, sortable: false,
      renderCell: (p) => <CoverageCell event={p.row} />,
    },
    {
      field: 'reason', headerName: 'Reason', flex: 1, sortable: false,
      renderCell: (p) => (p.row.reasonCategory
        ? <Pill tone={{ bg: `${reasonColor(p.row.reasonCategory)}22`, fg: NAVY }} dot dotColor={reasonColor(p.row.reasonCategory)}>{reasonLabel(p.row.reasonCategory)}</Pill>
        : <CellLine muted>—</CellLine>),
    },
    {
      field: 'status', headerName: 'Status', flex: 0.9, minWidth: 110, sortable: false,
      renderCell: (p) => (
        <Tooltip title={(p.row.status || '').replace(/_/g, ' ')}>
          <Chip size='small' label={p.row.status} color={STATUS_COLOR[p.row.status] || 'default'} sx={{ fontWeight: 700, height: 20, fontSize: 10, '& .MuiChip-label': { px: 0.8 } }} />
        </Tooltip>
      ),
    },
    {
      field: 'taggedOrderCount', headerName: 'Orders tagged', description: 'Shipments auto-attached to this event (matched its courier, geography & date window) — their delay is attributed to this disruption and excluded from SLA.', flex: 0.8, sortable: false, align: 'left', headerAlign: 'left',
      renderCell: (p) => {
        const n = p.row.taggedOrderCount ?? 0
        const pill = <Pill tone={n > 0 ? { bg: '#f3edfb', fg: '#6d28d9' } : { bg: '#eef1f5', fg: '#8A94A6' }}>{n.toLocaleString()}</Pill>
        return n > 0 ? <Box component='span' title='View tagged orders' sx={{ cursor: 'pointer' }} onClick={() => setTaggedFor(p.row)}>{pill}</Box> : pill
      },
    },
    {
      field: 'exports', headerName: 'Exports', minWidth: 90, flex: 0.5, sortable: false, align: 'center', headerAlign: 'center',
      renderCell: (p) => (
        <OrderRowActions tooltip='Export' actions={[
          { key: 'awbs', label: 'Export AWBs (CSV)', icon: <FileDownloadOutlinedIcon />, onClick: () => doExport(p.row.id, 'awbs') },
          { key: 'pins', label: 'Export pincodes (CSV)', icon: <FileDownloadOutlinedIcon />, onClick: () => doExport(p.row.id, 'pincodes') },
        ]} />
      ),
    },
  ]

  return (
    <FormProvider methods={methods} onSubmit={methods.handleSubmit(() => {})}>
    <Box>
      <PageTitle>KCI Management · Dashboard</PageTitle>

      {/* date filter */}
      <FilterCard>
        <FilterCardContent>
          <FilterRow>
            <FilterField sx={{ flex: '0 0 280px' }}>
              <FieldLabel>Date range</FieldLabel>
              <DateRangePicker showTextBox size='small' label='' placeholder='Select date range' maxDate={new Date()}
                defaultRange={from && to ? [{ startDate: parseKey(from), endDate: parseKey(to), key: 'selection' }] : []}
                onChange={(range) => {
                  if (range?.length && range[0].startDate && range[0].endDate) {
                    setFrom(toKey(range[0].startDate)); setTo(toKey(range[0].endDate))
                  }
                }} />
            </FilterField>
            <FilterActions>
              <ApplyButton type='button' variant='contained' size='small' onClick={applyFilters}>Filter</ApplyButton>
              <ResetButton type='button' variant='contained' size='small' onClick={resetFilters}>Reset</ResetButton>
            </FilterActions>
          </FilterRow>
        </FilterCardContent>
      </FilterCard>

      {initialLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12, gap: 1.5 }}>
          <CircularProgress size={34} sx={{ color: '#1565c0' }} />
          <Typography sx={{ fontSize: 13, color: MUTED }}>Loading dashboard…</Typography>
        </Box>
      )}

      {/* content — hidden (not unmounted) while the first load runs so queries keep going */}
      <Box sx={{ display: initialLoading ? 'none' : 'block' }}>
      {/* KPIs */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><StatTile label='Active events' value={kpis?.activeEvents ?? '—'} accent='#1565c0' /></Grid>
        <Grid item xs={6} md={3}><StatTile label='Pincodes impacted' value={pinDisplay} accent='#0f766e' /></Grid>
        <Grid item xs={6} md={3}><StatTile label='Orders tagged (in window)' value={taggedInWindow?.totalRecords != null ? taggedInWindow.totalRecords.toLocaleString() : '—'} accent='#6d28d9' /></Grid>
        <Grid item xs={6} md={3}><StatTile label='Avg. event duration' value={kpis?.avgEventDurationDays != null ? `${kpis.avgEventDurationDays} days` : '—'} accent='#b45309' /></Grid>
      </Grid>

      {/* 1) Recent KCI events — preview of the latest few; full list under Manage Events */}
      <Box sx={{ ...gridWrapSx, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid #F1F3F7', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#212B36' }}>Recent KCI events</Typography>
            <Typography sx={{ fontSize: 11, color: MUTED, mt: 0.25 }}>
              Latest {events.length}{eventsTotal > events.length ? ` of ${eventsTotal}` : ''} for the selected dates — open “Manage all events” for the complete, filterable list.
            </Typography>
          </Box>
          <Button size='small' endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none', fontWeight: 600, color: '#1565c0', whiteSpace: 'nowrap' }} onClick={() => navigate('/kci/events')}>
            Manage all events
          </Button>
        </Box>
        <ProzoDataGrid
          columns={eventColumns} rows={events} getRowId={(r) => r.id}
          pagination={false} hideFooterPagination getRowHeight={() => 'auto'}
          rowCount={events.length} sx={gridSx}
        />
      </Box>

      {/* 2) KCI Events Raised — daily trend */}
      <Box sx={{ mb: 2 }}>
        <SectionCard title={trendTitle('KCI Events Raised — daily trend', eventsDelta)} action={trendControls('eventsMonth', 'eventsCourier', eventsRaised.parents)}>
          {eventsRaised.data.length ? (
            <ResponsiveContainer width='100%' height={260}>
              <BarChart data={eventsRaised.data}>
                <CartesianGrid strokeDasharray='4 4' vertical={false} stroke='#E7EAF0' />
                <XAxis dataKey='date' tick={AXIS_TICK} tickFormatter={dayTick} tickLine={false} axisLine={{ stroke: '#E7EAF0' }} interval='preserveStartEnd' />
                <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} width={32} />
                <RTooltip cursor={{ fill: 'rgba(32,101,209,.05)' }} />
                <Legend iconType='circle' iconSize={9} align='left' verticalAlign='bottom' wrapperStyle={{ paddingTop: 10, marginLeft: 8 }} formatter={legendFmt} />
                {eventsSeries.map((p, i, arr) => <Bar key={p} dataKey={p} stackId='a' fill={courierColor(p, i)} maxBarSize={46} radius={i === arr.length - 1 ? [3, 3, 0, 0] : 0} />)}
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart label={`No KCI events raised in ${monthOptions.find((o) => o.value === eventsMonth)?.label || eventsMonth}`} />}
        </SectionCard>
      </Box>

      {/* 3) Shipments Impacted — daily trend */}
      <Box sx={{ mb: 2 }}>
        <SectionCard title={trendTitle('Shipments Impacted — daily trend (of ~30,000 shipments/day)', shipDelta)} action={trendControls('shipMonth', 'shipCourier', shipmentsTagged.parents)}>
          {shipmentsTagged.data.length ? (
            <ResponsiveContainer width='100%' height={260}>
              <BarChart data={shipmentsTagged.data}>
                <CartesianGrid strokeDasharray='4 4' vertical={false} stroke='#E7EAF0' />
                <XAxis dataKey='date' tick={AXIS_TICK} tickFormatter={dayTick} tickLine={false} axisLine={{ stroke: '#E7EAF0' }} interval='preserveStartEnd' />
                <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} width={32} />
                <RTooltip cursor={{ fill: 'rgba(32,101,209,.05)' }} />
                <Legend iconType='circle' iconSize={9} align='left' verticalAlign='bottom' wrapperStyle={{ paddingTop: 10, marginLeft: 8 }} formatter={legendFmt} />
                {shipSeries.map((p, i, arr) => <Bar key={p} dataKey={p} stackId='a' fill={courierColor(p, i)} maxBarSize={46} radius={i === arr.length - 1 ? [3, 3, 0, 0] : 0} />)}
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart label={`No shipments tagged in ${monthOptions.find((o) => o.value === shipMonth)?.label || shipMonth}`} />}
        </SectionCard>
      </Box>

      {/* 4) Callouts by reason category + Top impacted pincodes */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <ChartCard title='Callouts by reason category'>
            <ResponsiveContainer width='100%' height={230}>
              <BarChart data={reasonBars} margin={{ top: 24, right: 8, left: 0, bottom: 4 }}>
                <XAxis dataKey='name' tick={CAT_TICK} interval={0} tickLine={false} axisLine={{ stroke: '#E7EAF0' }} />
                <YAxis hide />
                <RTooltip cursor={false} />
                <Bar dataKey='value' maxBarSize={56} radius={[4, 4, 0, 0]}>
                  {reasonBars.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  <LabelList dataKey='value' position='top' style={barLabelStyle} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={5}>
          <ChartCard title='Top impacted pincodes'>
            <Stack spacing={0.5}>
              {Object.entries(topPincodes || {}).length === 0 && <Typography variant='body2' sx={{ color: MUTED }}>No data.</Typography>}
              {Object.entries(topPincodes || {}).map(([pin, n]) => (
                <Stack key={pin} direction='row' justifyContent='space-between' sx={{ py: 0.5, borderBottom: '1px solid #F1F3F7' }}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>{pin}</Typography>
                  <Typography variant='body2' sx={{ color: MUTED }}>{n} events</Typography>
                </Stack>
              ))}
            </Stack>
          </ChartCard>
        </Grid>
      </Grid>

      {/* 5) Impact by shipment direction + Events by courier scope */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <ChartCard title='Impact by shipment direction'>
            <ResponsiveContainer width='100%' height={220}>
              <BarChart data={directionBars} margin={{ top: 24, right: 8, left: 0, bottom: 4 }}>
                <XAxis dataKey='name' tick={CAT_TICK} tickLine={false} axisLine={{ stroke: '#E7EAF0' }} />
                <YAxis hide />
                <RTooltip cursor={false} />
                <Bar dataKey='value' maxBarSize={110} radius={[4, 4, 0, 0]}>
                  <Cell fill={PURPLE} /><Cell fill={PURPLE_LIGHT} />
                  <LabelList dataKey='value' position='top' style={barLabelStyle} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title='Events by courier scope'>
            <ResponsiveContainer width='100%' height={220}>
              <BarChart data={scopeBars} margin={{ top: 24, right: 8, left: 0, bottom: 4 }}>
                <XAxis dataKey='name' tick={CAT_TICK} tickLine={false} axisLine={{ stroke: '#E7EAF0' }} />
                <YAxis hide />
                <RTooltip cursor={false} />
                <Bar dataKey='value' maxBarSize={110} radius={[4, 4, 0, 0]}>
                  <Cell fill={PURPLE} /><Cell fill={PURPLE_LIGHT} />
                  <LabelList dataKey='value' position='top' style={barLabelStyle} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* 6) KCI impact history — by courier partner */}
      <SectionCard title='KCI impact history — by courier partner'>
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#637381' }}>
                <th style={{ padding: '8px 10px', fontWeight: 700 }}>Courier partner</th>
                <th style={{ padding: '8px 10px', fontWeight: 700, textAlign: 'right' }}>Events</th>
                <th style={{ padding: '8px 10px', fontWeight: 700, textAlign: 'right' }}>Shipments impacted</th>
                <th style={{ padding: '8px 10px', fontWeight: 700, textAlign: 'right' }}>Pincodes impacted</th>
                <th style={{ padding: '8px 10px', fontWeight: 700, textAlign: 'right' }}>Days impacted</th>
              </tr>
            </thead>
            <tbody>
              {courierRows.length === 0 && <tr><td colSpan={5} style={{ padding: 12, color: '#637381' }}>No data in this range.</td></tr>}
              {courierRows.map((r) => (
                <tr key={r.courier} style={{ borderTop: '1px solid #F1F3F7' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>{r.courier}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{r.events}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{r.shipments.toLocaleString()}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{r.panIndia ? 'Pan-India' : r.pincodes.toLocaleString()}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{r.days}</td>
                </tr>
              ))}
              {courierRows.length > 0 && (
                <tr style={{ borderTop: '2px solid #E7EAF0', fontWeight: 700 }}>
                  <td style={{ padding: '8px 10px' }}>Total</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{courierTotals.events}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{courierTotals.shipments.toLocaleString()}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{courierTotals.anyPan && courierTotals.pincodes === 0 ? 'Pan-India' : courierTotals.pincodes.toLocaleString()}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{courierTotals.days}</td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
        <Typography sx={{ fontSize: 11, color: MUTED, mt: 1 }}>Events, pincodes and days impacted come per courier from /by-courier; shipments are summed from the live daily rollup.</Typography>
      </SectionCard>
      </Box>
    </Box>
    {taggedFor && <TaggedOrdersModal event={taggedFor} onClose={() => setTaggedFor(null)} />}
    </FormProvider>
  )
}
